import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { DataTable, NavBar } from "@components/ui-kit";
import { Pagination } from "@components/ui-kit/table/pagination";
import { Trans, t } from "@lingui/macro";
import { UsersWhereColumn } from "@src/__generated__/urql-graphql";
import { ModalChangeUserPassword } from "@src/components/modules/users/listing/table/change-user-password/modal";
import { Icon } from "@src/components/ui-kit/Icon";
import type { UserType } from "@src/stores/models/Me";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useContext } from "react";
import { PageUsersCtx } from "./PageUsers";
import { QuickFilters } from "./StoreUsers";
import { TableUsers } from "./table/TableUsers";

export const CardListUsers = observer(function CardListUsers() {
  const store = useContext(PageUsersCtx);
  const { createUserModalStore, workspaceStore } = useStore();

  const state = useLocalObservable(() => ({
    get activeFilter() {
      return store.where.filters.find(
        (f) => f.column === UsersWhereColumn.Status,
      );
    },
    handleInviteUser(userType: UserType) {
      createUserModalStore.modalState.onOpen({
        userType,
        onSubmit() {
          if (store.userType !== userType) return;
          store.refetch?.();
        },
      });
    },
  }));

  return (
    <DataTable>
      <DataTable.NavBar>
        <HStack justifyContent="space-between">
          <NavBar>
            <NavBar.Item
              active={store.activeQuickFilter?.value?.[0] === QuickFilters.All}
              onClick={() => {
                state.activeFilter?.clear();
                store.activeQuickFilter?.setValue(QuickFilters.All);
              }}
            >
              <Trans>All</Trans>
            </NavBar.Item>
            <NavBar.Item
              active={
                store.activeQuickFilter?.value?.[0] === QuickFilters.Active
              }
              onClick={() => {
                state.activeFilter?.setValue(QuickFilters.Active);
                store.activeQuickFilter?.setValue(QuickFilters.Active);
              }}
            >
              <Trans>Active</Trans>
            </NavBar.Item>
            <NavBar.Item
              active={
                store.activeQuickFilter?.value?.[0] === QuickFilters.Archived
              }
              onClick={() => {
                state.activeFilter?.setValue(QuickFilters.Archived);
                store.activeQuickFilter?.setValue(QuickFilters.Archived);
              }}
            >
              <Trans>Deactivated</Trans>
            </NavBar.Item>
          </NavBar>
          {can("user_create", "user_scopeCreate") && (
            <HStack spacing="3">
              <Menu>
                <MenuButton>
                  <Button leftIcon={<Icon name="plus" />}>
                    <Trans>Invite</Trans>
                  </Button>
                </MenuButton>
                <MenuList>
                  {can("user_create") && (
                    <MenuItem
                      onClick={() => state.handleInviteUser("internal")}
                    >
                      <Trans>User</Trans>
                    </MenuItem>
                  )}
                  {can("user_scopeCreate") && (
                    <MenuItem onClick={() => state.handleInviteUser("client")}>
                      <Trans>Client</Trans>
                    </MenuItem>
                  )}
                  {workspaceStore.settings?.partnerAccessEnabled &&
                    can("user_scopeCreate") && (
                      <MenuItem
                        onClick={() => state.handleInviteUser("partner")}
                      >
                        <Trans>Partner</Trans>
                      </MenuItem>
                    )}
                </MenuList>
              </Menu>
            </HStack>
          )}
        </HStack>
      </DataTable.NavBar>
      <DataTable.Filter>
        <FiltersPanel<UsersWhereColumn>
          filters={store.where}
          searchPlaceholder={t`Search by name, codename, team, role `}
          searchDefaultValue={store.searchTerm}
          onSearch={(val) => {
            store.searchTerm = val;
            store.pagination.resetPage();
          }}
        />
      </DataTable.Filter>

      <TableUsers />
      <Pagination store={store.pagination} />

      <ModalChangeUserPassword store={store.changeUserPasswordStore} />
    </DataTable>
  );
});
